import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext';
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import PromoBanner from './components/PromoBanner';
import ScrollToTop from './components/ScrollToTop';
import Home from './pages/Home';
import Rentals from './pages/Rentals';
import Contact from './pages/Contact';
import ProductDetails from './pages/ProductDetails';
import Booking from './pages/Booking';
import Payment from './pages/Payment';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsAndConditions from './pages/TermsAndConditions';
import AdminLogin from './pages/admin/AdminLogin';
import AdminDashboard from './pages/admin/AdminDashboard';
import { useAuth } from './context/AuthContext';
import { useNavigationCleanup } from './hooks/useNavigationCleanup';
import { useAppManifest } from './hooks/useAppManifest';
import './styles/mobile.css';

const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { state, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }
  
  return state.isAuthenticated ? <>{children}</> : <Navigate to="/admin/login" />;
};

const PublicRoutes: React.FC = () => (
  <>
    <PromoBanner />
    <Navbar />
    <div className="flex flex-col min-h-screen">
      <div className="flex-grow">
        <Routes>
          <Route path="/" element={<Navigate to="/home" replace />} />
          <Route path="/home" element={<Home />} />
          <Route path="/rentals" element={<Rentals />} />
          <Route path="/product" element={<ProductDetails />} />
          <Route path="/booking" element={<Booking />} />
          <Route path="/payment/:uuid" element={<Payment />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route path="/terms" element={<TermsAndConditions />} />
          <Route path="/admin/login" element={<AdminLogin />} />
          <Route path="/admin/*" element={<Navigate to="/admin/login" />} />
        </Routes>
      </div>
      <Footer />
    </div>
  </>
);

const AdminRoutes: React.FC = () => (
  <>
    <Routes>
      {/* Public Pages - accessible to logged-in admins */}
      <Route path="/" element={<Navigate to="/home" replace />} />
      <Route path="/home" element={
        <>
          <PromoBanner />
          <Navbar />
          <div className="flex flex-col min-h-screen">
            <div className="flex-grow">
              <Home />
            </div>
            <Footer />
          </div>
        </>
      } />
      <Route path="/rentals" element={
        <>
          <PromoBanner />
          <Navbar />
          <div className="flex flex-col min-h-screen">
            <div className="flex-grow">
              <Rentals />
            </div>
            <Footer />
          </div>
        </>
      } />
      <Route path="/product" element={
        <>
          <PromoBanner />
          <Navbar />
          <div className="flex flex-col min-h-screen">
            <div className="flex-grow">
              <ProductDetails />
            </div>
            <Footer />
          </div>
        </>
      } />
      <Route path="/booking" element={
        <>
          <PromoBanner />
          <Navbar />
          <div className="flex flex-col min-h-screen">
            <div className="flex-grow">
              <Booking />
            </div>
            <Footer />
          </div>
        </>
      } />
      <Route path="/payment/:uuid" element={
        <>
          <PromoBanner />
          <Navbar />
          <div className="flex flex-col min-h-screen">
            <div className="flex-grow">
              <Payment />
            </div>
            <Footer />
          </div>
        </>
      } />
      <Route path="/contact" element={
        <>
          <PromoBanner />
          <Navbar />
          <div className="flex flex-col min-h-screen">
            <div className="flex-grow">
              <Contact />
            </div>
            <Footer />
          </div>
        </>
      } />
      <Route path="/privacy" element={
        <>
          <PromoBanner />
          <Navbar />
          <div className="flex flex-col min-h-screen">
            <div className="flex-grow">
              <PrivacyPolicy />
            </div>
            <Footer />
          </div>
        </>
      } />
      <Route path="/terms" element={
        <>
          <PromoBanner />
          <Navbar />
          <div className="flex flex-col min-h-screen">
            <div className="flex-grow">
              <TermsAndConditions />
            </div>
            <Footer />
          </div>
        </>
      } />
      
      {/* Admin Routes */}
      <Route path="/admin/login" element={<AdminLogin />} />
      <Route path="/admin" element={
        <ProtectedRoute>
          <AdminDashboard />
        </ProtectedRoute>
      } />
      <Route path="/admin/dashboard/*" element={
        <ProtectedRoute>
          <AdminDashboard />
        </ProtectedRoute>
      } />
      <Route path="/admin/*" element={
        <ProtectedRoute>
          <AdminDashboard />
        </ProtectedRoute>
      } />
    </Routes>
  </>
);

const AppContent: React.FC = () => {
  const { state, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }
  
  return (
    <Router>
      <ScrollToTop />
      <AppManifest />
      <NavigationCleanupWrapper>
        {state.isAuthenticated ? <AdminRoutes /> : <PublicRoutes />}
      </NavigationCleanupWrapper>
    </Router>
  );
};

const AppManifest: React.FC = () => {
  // Point "Add to Home Screen" at the admin app while on /admin/*
  useAppManifest();
  return null;
};

const NavigationCleanupWrapper: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // Clear booking data when navigating away from booking flow
  useNavigationCleanup();
  return <>{children}</>;
};

function App() {
  return (
    <AuthProvider>
      <div className="min-h-screen bg-gray-50">
        <AppContent />
      </div>
    </AuthProvider>
  );
}

export default App;
